import { ItemTypeEnum } from '@msc-b2c/interfaces/enums';
import styles from './cardfooter.module.scss';
import { CartBodyLabelsProps, CartItem } from '@msc-b2c/interfaces/types';
import {
  MyMscExcursion,
  OBSObjI,
  PrepaidBookingPricesMyMSCResponse,
} from '@msc-b2c/interfaces/interfaces';
import { getPriceByTypeAndCode } from '../../../../utils/myAreaCartUtils';
import { SkeletonLoader, Text } from '@msc-b2c/shared-components';
import {
  formatPriceWithoutRounding,
  getFormatPriceCurrencyOrder,
  replacePlaceholders,
  RootState,
} from '@msc-b2c/shared-utils';
import { useSelector } from 'react-redux';

export function CardFooter({
  item,
  labels,
  service,
  relatedItems = [],
  isPricesLoading,
}: Readonly<{
  item: CartItem;
  labels: Readonly<CartBodyLabelsProps>;
  service: OBSObjI | MyMscExcursion;
  /** Linked package lines (e.g. minor ALLINC) whose prices are summed into this card total. */
  relatedItems?: CartItem[];
  isPricesLoading?: boolean;
}>) {
  const { prices, confirmation: confirmationResponse } = useSelector(
    (state: RootState) => state.myAreaCart
  );
  let cartItemType: ItemTypeEnum | null = null;
  if (item.itemType === 'EXCURSION') {
    cartItemType = ItemTypeEnum.EXCURSION;
  } else if (item.itemType === 'ONBOARD_SERVICE') {
    cartItemType = ItemTypeEnum.ONBOARD_SERVICE;
  }
  const { is3dsBookingSuccess } = useSelector((state: RootState) => state.payment);
  const effectivePrices = (is3dsBookingSuccess ? confirmationResponse : prices) ?? null;
  const primaryItemPrices = getPriceByTypeAndCode(cartItemType, item.code, effectivePrices);

  // Sum the primary line with its linked package line(s) so the combined card shows one total.
  const serviceItemPrices = (() => {
    if (!relatedItems.length) {
      return primaryItemPrices;
    }
    const toNumber = (value?: string) => {
      const parsed = Number.parseFloat(value ?? '');
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    let summedPrice = toNumber(primaryItemPrices?.price);
    let summedStrike = primaryItemPrices?.strikeThroughPrice
      ? toNumber(primaryItemPrices.strikeThroughPrice)
      : toNumber(primaryItemPrices?.price);
    let hasStrike = Boolean(primaryItemPrices?.strikeThroughPrice);
    relatedItems.forEach((relatedItem) => {
      const relatedType =
        relatedItem.itemType === 'EXCURSION'
          ? ItemTypeEnum.EXCURSION
          : relatedItem.itemType === 'ONBOARD_SERVICE'
          ? ItemTypeEnum.ONBOARD_SERVICE
          : null;
      const relatedPrices = getPriceByTypeAndCode(relatedType, relatedItem.code, effectivePrices);
      if (!relatedPrices) {
        return;
      }
      summedPrice += toNumber(relatedPrices.price);
      summedStrike += relatedPrices.strikeThroughPrice
        ? toNumber(relatedPrices.strikeThroughPrice)
        : toNumber(relatedPrices.price);
      hasStrike = hasStrike || Boolean(relatedPrices.strikeThroughPrice);
    });
    if (!primaryItemPrices && !relatedItems.length) {
      return primaryItemPrices;
    }
    return {
      price: String(summedPrice),
      ...(hasStrike && summedStrike > summedPrice && { strikeThroughPrice: String(summedStrike) }),
    };
  })();
  const discounted = !!serviceItemPrices?.strikeThroughPrice;

  const getDiscountLabel = (): string | undefined => {
    if (!discounted || isPricesLoading) {
      return undefined;
    }

    const cartPrices: PrepaidBookingPricesMyMSCResponse | null = is3dsBookingSuccess
      ? confirmationResponse ?? null
      : prices;
    if (!cartPrices) {
      return labels.discountDescriptionText;
    }

    const priceEntry = getItemPriceEntry(cartPrices);
    const total = priceEntry?.price?.total;
    const hasPromotionAmount = (amount?: string) => Number(amount || 0) > 0;
    const itemHasPromo = hasPromotionAmount(total?.promotionAmount);

    const { isFree: couponIsFree, percentage: couponPercentage } = getCouponMeta(cartPrices);

    // If there is a promotion on the item, keep using the promotion label.
    if (itemHasPromo && labels.discountDescriptionText) {
      return labels.discountDescriptionText;
    }

    // Coupon label selection by typology
    if (couponIsFree && labels.appliedCouponFreeItemLabel) {
      return labels.appliedCouponFreeItemLabel;
    }

    if (couponPercentage && labels.appliedCouponPercentageLabel) {
      const numericPercentage = Number(couponPercentage);
      const formattedPercentage = Number.isNaN(numericPercentage)
        ? couponPercentage
        : String(Math.round(numericPercentage));

      return replacePlaceholders(labels.appliedCouponPercentageLabel, {
        discountPercentage: formattedPercentage,
      });
    }

    if (!couponIsFree && !couponPercentage && labels.appliedCouponAmountLabel) {
      // Combined packages span multiple price entries; sum the discount across all lines so the
      // label matches the saving shown in the summary sidebar (not just the adult line).
      const combinedDiscountAmount = getCombinedDiscountAmount(cartPrices);
      const formattedDiscountAmount =
        combinedDiscountAmount > 0
          ? getFormatPriceCurrencyOrder(formatPriceWithoutRounding(String(combinedDiscountAmount)))
          : '';
      return replacePlaceholders(labels.appliedCouponAmountLabel, {
        discountAmount: formattedDiscountAmount,
      });
    }

    return undefined;
  };

  const getPriceEntryByTypeAndCode = (
    cartPrices: PrepaidBookingPricesMyMSCResponse | null,
    itemType: CartItem['itemType'],
    code?: string
  ) => {
    if (!cartPrices) return null;

    const items =
      itemType === 'EXCURSION'
        ? cartPrices.excursions
        : itemType === 'ONBOARD_SERVICE'
        ? cartPrices.onboardServices
        : null;

    return items?.find((entry) => entry.code === code) || null;
  };

  const getItemPriceEntry = (cartPrices: PrepaidBookingPricesMyMSCResponse | null) =>
    getPriceEntryByTypeAndCode(cartPrices, item.itemType, item.code);

  // Sums the per-line coupon discount of the primary line and any linked package line(s).
  const getCombinedDiscountAmount = (
    cartPrices: PrepaidBookingPricesMyMSCResponse | null
  ): number => {
    if (!cartPrices) return 0;

    const lines = [item, ...relatedItems];
    return lines.reduce((sum, line) => {
      const entry = getPriceEntryByTypeAndCode(cartPrices, line.itemType, line.code);
      const amount = Number(entry?.price?.total?.discountAmount ?? 0);
      return sum + (Number.isNaN(amount) ? 0 : amount);
    }, 0);
  };

  const getCouponMeta = (
    cartPrices: PrepaidBookingPricesMyMSCResponse | null
  ): { isFree: boolean; percentage?: string } => {
    const hasPromotionAmount = (amount?: string) => Number(amount || 0) > 0;
    const result = { isFree: false, percentage: undefined as string | undefined };

    const priceEntry = getItemPriceEntry(cartPrices);
    if (!priceEntry) return result;

    const checkCouponOnPassengersOrCabins = () => {
      const passengerWithCoupon = priceEntry.passengers?.find(
        (pax) => pax.couponCode && !hasPromotionAmount(pax.price?.total?.promotionAmount)
      );

      if (passengerWithCoupon?.couponCode) {
        result.isFree = Boolean(passengerWithCoupon.couponCode.isFree);
        result.percentage = passengerWithCoupon.couponCode.percentage;
        return true;
      }

      const cabinWithCoupon = priceEntry.cabins?.find(
        (cabin) => cabin.couponCode && !hasPromotionAmount(cabin.price?.total?.promotionAmount)
      );

      if (cabinWithCoupon?.couponCode) {
        result.isFree = Boolean(cabinWithCoupon.couponCode.isFree);
        result.percentage = cabinWithCoupon.couponCode.percentage;
        return true;
      }

      return false;
    };

    if (checkCouponOnPassengersOrCabins()) {
      return result;
    }

    const allItems = [...(cartPrices?.excursions ?? []), ...(cartPrices?.onboardServices ?? [])];

    const entryWithPercentage = allItems.find(
      (entry) =>
        entry.passengers?.some((pax) => pax.couponCode?.percentage) ||
        entry.cabins?.some((cabin) => cabin.couponCode?.percentage)
    );

    if (entryWithPercentage) {
      const passengerWithPercentage = entryWithPercentage.passengers?.find(
        (pax) => pax.couponCode?.percentage
      );
      if (passengerWithPercentage?.couponCode?.percentage) {
        result.percentage = passengerWithPercentage.couponCode.percentage;
        return result;
      }

      const cabinWithPercentage = entryWithPercentage.cabins?.find(
        (cabin) => cabin.couponCode?.percentage
      );
      if (cabinWithPercentage?.couponCode?.percentage) {
        result.percentage = cabinWithPercentage.couponCode.percentage;
      }
    }

    return result;
  };

  return (
    <div className={styles['card-footer']}>
      <div className={styles['card-footer__prices']}>
        <div className={styles['card-footer__prices-price']}>
          {cartItemType &&
            service?.code &&
            (isPricesLoading ? (
              <>
                {discounted && (
                  <SkeletonLoader
                    width="80px"
                    height="18px"
                    styleOption="cmp-skeleton__loader"
                  />
                )}
                <SkeletonLoader
                  width="100px"
                  height="26px"
                  styleOption="cmp-skeleton__loader"
                />
              </>
            ) : (
              <>
                {discounted && (
                  <Text
                    text={getFormatPriceCurrencyOrder(
                      formatPriceWithoutRounding(String(serviceItemPrices?.strikeThroughPrice || 0))
                    )}
                    styleOption={`${styles['card-footer__prices-amount']} discounted body-default`}
                  />
                )}
                <Text
                  text={getFormatPriceCurrencyOrder(
                    formatPriceWithoutRounding(String(serviceItemPrices?.price || 0))
                  )}
                  styleOption={`${styles['card-footer__prices-discount']} header-s`}
                />
              </>
            ))}
        </div>
        {!isPricesLoading && discounted && getDiscountLabel() && (
          <Text
            text={getDiscountLabel()}
            styleOption={`${styles['card-footer__status-success']} body-small`}
          />
        )}
      </div>
    </div>
  );
}
